"use client";

import {
	Tab as AriaTab,
	TabList as AriaTabList,
	TabListProps as AriaTabListProps,
	TabPanel as AriaTabPanel,
	TabPanelProps as AriaTabPanelProps,
	TabProps as AriaTabProps,
	Tabs as AriaTabs,
	TabsProps as AriaTabsProps,
	composeRenderProps,
} from "react-aria-components";

import { cn } from "@/utils/cn";

function Tabs({ className, ...props }: AriaTabsProps) {
	return (
		<AriaTabs
			className={composeRenderProps(className, className =>
				cn(
					"group flex flex-col gap-2",
					/* Orientation */
					"data-[orientation=vertical]:flex-row",
					className,
				),
			)}
			{...props}
		/>
	);
}

const TabList = <T extends object>({ className, ...props }: AriaTabListProps<T>) => (
	<AriaTabList
		className={composeRenderProps(className, className =>
			cn(
				"inline-flex items-center justify-start gap-6 w-full border-b border-border",
				/* Orientation */
				"data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-start data-[orientation=vertical]:gap-2",
				"data-[orientation=vertical]:border-b-0 data-[orientation=vertical]:border-r data-[orientation=vertical]:h-auto",
				className,
			),
		)}
		{...props}
	/>
);

const Tab = ({ className, ...props }: AriaTabProps) => (
	<AriaTab
		className={composeRenderProps(className, className =>
			cn(
				"relative inline-flex items-center justify-center whitespace-nowrap px-1 py-4 text-sm font-medium outline-none transition-all cursor-pointer focus-subtle",
				/* Disabled */
				"data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
				/* Selected */
				"data-[selected]:text-foreground",
				/* Hover */
				"hover:text-foreground/80 hover:after:opacity-50",
				/* After element for bottom border */
				"after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-foreground after:opacity-0 after:transition-opacity",
				/* Selected state for bottom border */
				"data-[selected]:after:opacity-100",
				/* Orientation */
				"group-data-[orientation=vertical]:w-full group-data-[orientation=vertical]:justify-start",
				"group-data-[orientation=vertical]:px-4 group-data-[orientation=vertical]:py-2",
				"group-data-[orientation=vertical]:after:left-auto group-data-[orientation=vertical]:after:right-0",
				"group-data-[orientation=vertical]:after:top-0 group-data-[orientation=vertical]:after:h-full",
				"group-data-[orientation=vertical]:after:w-0.5 group-data-[orientation=vertical]:after:opacity-0",
				"group-data-[orientation=vertical]:data-[selected]:after:opacity-100",
				className,
			),
		)}
		{...props}
	/>
);

const TabPanel = ({ className, ...props }: AriaTabPanelProps) => (
	<AriaTabPanel
		className={composeRenderProps(className, className =>
			cn("py-8 px-4 ring-offset-background focus-visible:outline-none focus-subtle", className),
		)}
		{...props}
	/>
);

export { Tab, TabList, TabPanel, Tabs };
